import { useState, useEffect } from 'react';
import { ArrowLeft, User, CreditCard, Check, MapPin, Clock, X } from 'lucide-react';
import { Button } from './Button';
import { useBooking } from '../hooks/useBooking';
import { useNavigation } from '../hooks/useNavigation';

interface PassengerData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  age: string;
  gender: string;
}

interface PassengerInfo extends PassengerData {
  seatNumber: string;
}

type BookingViewModel = {
  company: string;
  rating: number;
  ratingCount: number;
  price: number;
  departureTime: string;
  departureLocation: string;
  arrivalTime: string;
  arrivalLocation: string;
  duration: string;
  amenities: any[];
  availableSeats: number;
  discount?: any;
  vehicleType: string;
};



export const PassengerForm = () => {
  console.log('🔷 PASSENGER_FORM_RENDER_START');
  
  // Obtener datos del store en lugar de props
  const { selectedBooking, continueToPayment } = useBooking();
  const { goToSearch } = useNavigation();

  console.log('🔷 PASSENGER_FORM_BOOKING_DATA', {
    hasSelectedBooking: !!selectedBooking,
    selectedBooking: selectedBooking,
    hasBookingData: !!selectedBooking,
    hasSeatSelection: !!selectedBooking?.selectedSeats,
    seatCount: selectedBooking?.selectedSeats?.length ?? 0,
  });

  // Validar que hay datos con useEffect
  useEffect(() => {
    console.log('🔷 PASSENGER_FORM_EFFECT_CHECK', {
      hasSelectedBooking: !!selectedBooking,
    });
    
    if (!selectedBooking) {
      console.warn('⚠️ PASSENGER_FORM_REDIRECT_NO_BOOKING');
      goToSearch();
    }
  }, [selectedBooking, goToSearch]);

  // 🔧 PREVIEW/REAL APP COMPATIBILITY FIX
  // In preview: selectedBooking exists directly (no nested bookingData)
  // In real app: selectedBooking.bookingData exists
  // This makes it work in both environments without breaking either
  const hasAnyBookingData = selectedBooking && (selectedBooking || selectedBooking);
  
  console.log('🔧 ENVIRONMENT_CHECK', {
    hasSelectedBooking: !!selectedBooking,
    hasNestedBookingData: !!selectedBooking,
    hasAnyBookingData: !!hasAnyBookingData,
    environment: selectedBooking ? 'REAL_APP' : 'PREVIEW',
  });

  // Si no hay ningún tipo de booking data, mostrar loading mientras redirige
  if (!hasAnyBookingData) {
    console.warn('⚠️ PASSENGER_FORM_LOADING_STATE', {
      hasSelectedBooking: !!selectedBooking,
      hasBookingData: !!selectedBooking,
    });
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF6A2A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6B7280] font-medium">Redirigiendo...</p>
        </div>
      </div>
    );
  }

  // 🔧 EFFECTIVE BOOKING DATA - Compatible with both environments
  // Real app: use bookingData from selectedBooking.bookingData
  // Preview: use selectedBooking directly as fallback
// 🔧 NORMALIZE booking data (works if data is flat or nested)
const bookingRaw: any =
  (selectedBooking as any)?.bookingData ??
  (selectedBooking as any)?.booking ??
  selectedBooking ??
  null;

const effectiveSelectedSeats =
  (selectedBooking as any)?.selectedSeats ??
  (selectedBooking as any)?.seats ??
  bookingRaw?.selectedSeats ??
  bookingRaw?.seats ??
  [];

const safeBookingData: BookingViewModel = bookingRaw
  ? {
      company: bookingRaw.company ?? 'Sin nombre',
      rating: bookingRaw.rating ?? 0,
      ratingCount: bookingRaw.ratingCount ?? 0,
      price: bookingRaw.price ?? 0,
      departureTime: bookingRaw.departureTime ?? '--:--',
      departureLocation: bookingRaw.departureLocation ?? 'Sin ubicación',
      arrivalTime: bookingRaw.arrivalTime ?? '--:--',
      arrivalLocation: bookingRaw.arrivalLocation ?? 'Sin ubicación',
      duration: bookingRaw.duration ?? '0h',
      amenities: bookingRaw.amenities ?? [],
      availableSeats: bookingRaw.availableSeats ?? 0,
      discount: bookingRaw.discount,
      vehicleType: bookingRaw.vehicleType ?? 'Bus',
    }
  : {
      company: 'Sin nombre',
      rating: 0,
      ratingCount: 0,
      price: 0,
      departureTime: '--:--',
      departureLocation: 'Sin ubicación',
      arrivalTime: '--:--',
      arrivalLocation: 'Sin ubicación',
      duration: '0h',
      amenities: [],
      availableSeats: 0,
      discount: undefined,
      vehicleType: 'Bus',
    };

const safeSelectedSeats = effectiveSelectedSeats ?? [];


  const [currentStep] = useState<'seats' | 'passengers' | 'payment'>('passengers');
  const [passengers, setPassengers] = useState<PassengerData[]>(
    safeSelectedSeats.map(() => ({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      age: '',
      gender: '',
    }))
  );

  const totalPrice = safeBookingData.price * safeSelectedSeats.length;

  const handlePassengerChange = (index: number, field: keyof PassengerData, value: string) => {
    const newPassengers = [...passengers];
    newPassengers[index] = { ...newPassengers[index], [field]: value };
    setPassengers(newPassengers);
  };

  const handleContinueToPayment = () => {
    console.log('🔷 HANDLE_CONTINUE_TO_PAYMENT_START');
    console.log('🔷 PASSENGERS_DATA_TO_SAVE', {
      passengers: passengers,
      passengersCount: passengers.length,
      hasData: passengers.length > 0,
      firstPassenger: passengers[0] ?? null,
    });
    
    try {
const payload: PassengerInfo[] = passengers.map((p, i) => ({
  ...p,
  seatNumber: safeSelectedSeats[i] ?? `Seat-${i + 1}`,
}));

continueToPayment(payload);      console.log('✅ CONTINUE_TO_PAYMENT_CALLED');
    } catch (error) {
      console.error('❌ CONTINUE_TO_PAYMENT_ERROR', error);
    }
  };

  const steps = [
    { id: 'seats', label: 'Asientos de Ida', icon: MapPin, completed: true },
    { id: 'passengers', label: 'Pasajeros', icon: User, completed: false },
    { id: 'payment', label: 'Pago', icon: CreditCard, completed: false },
  ];

  return (
    <>
      {/* Mobile View */}
      <div className="lg:hidden min-h-screen bg-[#F7F8FA]">
        {/* Sticky Header */}
        <div className="sticky top-0 z-30 bg-[#D97540] text-white shadow-md">
          <div className="flex items-center gap-3 p-4">
            <button
              onClick={goToSearch}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="font-bold text-base">Datos para la compra</h1>
              <p className="text-xs opacity-90">
                {safeBookingData.departureLocation.split(' ')[0]} → {safeBookingData.arrivalLocation.split(' ')[0]}
              </p>
            </div>
            <button className="w-9 h-9 flex items-center justify-center">
              <div className="w-6 h-0.5 bg-white rounded-full mb-1"></div>
              <div className="w-6 h-0.5 bg-white rounded-full mb-1"></div>
              <div className="w-6 h-0.5 bg-white rounded-full"></div>
            </button>
          </div>

          {/* Trip Info */}
          <div className="px-4 pb-3 flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>Tu viaje de ida</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{safeBookingData.departureTime} - {safeBookingData.arrivalTime}</span>
            </div>
            <button className="text-xs underline">Detalles</button>
          </div>

          {/* Step Indicator */}
          <div className="px-4 pb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span className="text-sm font-semibold">Pasajeros</span>
            </div>
            <span className="text-xs">Paso 2 de 3</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 pb-32">
          {/* Passengers Section */}
          <div className="mb-4">
            <h2 className="text-lg font-bold text-[#111827] mb-1">¿Quiénes viajan?</h2>
            <p className="text-sm text-[#6B7280] mb-4">{safeSelectedSeats.length} a bordo</p>

            <div className="space-y-4">
{safeSelectedSeats.map((seat: string | number, index: number) => (
                <div key={seat} className="bg-white rounded-[16px] border border-[#E5E7EB] p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#FF6A2A] rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#111827]">Pasajero {index + 1}</h3>
                        <span className="text-xs text-[#6B7280] bg-[#FFF5F0] px-2 py-0.5 rounded-[6px] border border-[#FF6A2A] font-bold text-[#FF6A2A]">
                          {seat}
                        </span>
                      </div>
                    </div>
                    {index > 0 && (
                      <button className="text-[#6B7280] hover:text-[#111827]">
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-[#6B7280] mb-1">
                        Nombre (s)
                      </label>
                      <input
                        type="text"
                        value={passengers[index]?.firstName || ''}
                        onChange={(e) => handlePassengerChange(index, 'firstName', e.target.value)}
                        className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                        placeholder="Ingresa nombre"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-[#6B7280] mb-1">
                        Apellido Paterno
                      </label>
                      <input
                        type="text"
                        value={passengers[index]?.lastName || ''}
                        onChange={(e) => handlePassengerChange(index, 'lastName', e.target.value)}
                        className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                        placeholder="Ingresa apellido"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-[#6B7280] mb-1">
                          Edad
                        </label>
                        <input
                          type="number"
                          value={passengers[index]?.age || ''}
                          onChange={(e) => handlePassengerChange(index, 'age', e.target.value)}
                          className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                          placeholder="Edad"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#6B7280] mb-1">
                          Género
                        </label>
                        <select
                          value={passengers[index]?.gender || ''}
                          onChange={(e) => handlePassengerChange(index, 'gender', e.target.value)}
                          className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                        >
                          <option value="">Seleccionar</option>
                          <option value="M">Masculino</option>
                          <option value="F">Femenino</option>
                          <option value="O">Otro</option>
                        </select>
                      </div>
                    </div>

                    {index === 0 && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-[#6B7280] mb-1">
                            Correo electrónico
                          </label>
                          <input
                            type="email"
                            value={passengers[index]?.email || ''}
                            onChange={(e) => handlePassengerChange(index, 'email', e.target.value)}
                            className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                            placeholder="correo@ejemplo.com"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-[#6B7280] mb-1">
                            Teléfono
                          </label>
                          <input
                            type="tel"
                            value={passengers[index]?.phone || ''}
                            onChange={(e) => handlePassengerChange(index, 'phone', e.target.value)}
                            className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                            placeholder="10 dígitos"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Fixed Bottom CTA */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#E5E7EB] p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.1)]">
          <button
            onClick={handleContinueToPayment}
            className="w-full py-3.5 bg-[#FF6A2A] hover:bg-[#C2410C] text-white font-semibold rounded-[12px] transition-colors flex items-center justify-center gap-2"
          >
            Siguiente
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block min-h-screen bg-[#F7F8FA]">
        {/* Top Bar */}
        <div className="bg-white border-b border-[#E5E7EB] py-4">
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={goToSearch}
                className="flex items-center gap-2 text-[#6B7280] hover:text-[#111827] transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Volver</span>
              </button>
              <div className="border-l border-[#E5E7EB] pl-4">
                <h1 className="font-bold text-[#111827]">
                  {safeBookingData.departureLocation.split(' ')[0]} → {safeBookingData.arrivalLocation.split(' ')[0]}
                </h1>
                <p className="text-sm text-[#6B7280]">
                  {safeBookingData.departureTime} - {safeBookingData.arrivalTime} • {safeBookingData.duration}
                </p>
              </div>
            </div>
            <button className="px-4 py-2 text-sm text-[#FF6A2A] hover:bg-[#FFF5F0] rounded-[10px] transition-colors">
              Detalles
            </button>
          </div>
        </div>

        {/* Stepper */}
        <div className="bg-white border-b border-[#E5E7EB]">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-center">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = step.id === currentStep;
                const isCompleted = step.completed;

                return (
                  <div key={step.id} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                          isCompleted
                            ? 'bg-[#10B981] border-[#10B981]'
                            : isActive
                            ? 'bg-[#FF6A2A] border-[#FF6A2A]'
                            : 'bg-white border-[#E5E7EB]'
                        }`}
                      >
                        {isCompleted ? (
                          <Check className="w-6 h-6 text-white" />
                        ) : (
                          <Icon
                            className={`w-6 h-6 ${
                              isActive ? 'text-white' : 'text-[#6B7280]'
                            }`}
                          />
                        )}
                      </div>
                      <p
                        className={`mt-2 text-sm font-medium ${
                          isActive ? 'text-[#111827]' : 'text-[#6B7280]'
                        }`}
                      >
                        {step.label}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`w-32 h-0.5 mx-4 mb-6 ${
                          isCompleted ? 'bg-[#10B981]' : 'bg-[#E5E7EB]'
                        }`}
                      ></div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="text-center mt-2">
              <p className="text-sm text-[#6B7280]">Paso 2 de 3</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-12 gap-8">
            {/* Left Column - Passenger Forms */}
            <div className="col-span-8">
              <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-6">
                <h2 className="text-xl font-bold text-[#111827] mb-1">
                  Tus asientos - Piso 1
                </h2>
                <p className="text-sm text-[#6B7280] mb-6">
                  Elige los asientos que necesites, en seguida te solicitaremos los datos de los pasajeros
                </p>

                <div className="space-y-6">
                  <h3 className="font-bold text-[#111827] flex items-center gap-2">
                    <User className="w-5 h-5 text-[#FF6A2A]" />
                    ¿Quiénes viajan?
                    <span className="text-sm font-normal text-[#6B7280] ml-2">
                      {safeSelectedSeats.length} a bordo
                    </span>
                  </h3>

{safeSelectedSeats.map((seat: string | number, index: number) => (
                    <div
                      key={seat}
                      className="border border-[#E5E7EB] rounded-[12px] p-5"
                    >
                      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[#E5E7EB]">
                        <div className="w-10 h-10 bg-[#1E3A8A] text-white rounded-full flex items-center justify-center font-bold">
                          {seat}
                        </div>
                        <div>
                          <h4 className="font-bold text-[#111827]">Pasajero {index + 1}</h4>
                          <p className="text-xs text-[#6B7280]">Asiento {seat}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[#111827] mb-2">
                            Nombre (s)
                          </label>
                          <input
                            type="text"
                            value={passengers[index]?.firstName || ''}
                            onChange={(e) =>
                              handlePassengerChange(index, 'firstName', e.target.value)
                            }
                            className="w-full px-4 py-3 border border-[#E5E7EB] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                            placeholder="Nombre"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#111827] mb-2">
                            Apellido Paterno
                          </label>
                          <input
                            type="text"
                            value={passengers[index]?.lastName || ''}
                            onChange={(e) =>
                              handlePassengerChange(index, 'lastName', e.target.value)
                            }
                            className="w-full px-4 py-3 border border-[#E5E7EB] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                            placeholder="Apellido"
                          />
                        </div>

                        {index === 0 && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-[#111827] mb-2">
                                Correo electrónico
                              </label>
                              <input
                                type="email"
                                value={passengers[index]?.email || ''}
                                onChange={(e) =>
                                  handlePassengerChange(index, 'email', e.target.value)
                                }
                                className="w-full px-4 py-3 border border-[#E5E7EB] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                                placeholder="correo@ejemplo.com"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-[#111827] mb-2">
                                Teléfono
                              </label>
                              <input
                                type="tel"
                                value={passengers[index]?.phone || ''}
                                onChange={(e) =>
                                  handlePassengerChange(index, 'phone', e.target.value)
                                }
                                className="w-full px-4 py-3 border border-[#E5E7EB] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                                placeholder="10 dígitos"
                              />
                            </div>
                          </>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-[#111827] mb-2">
                            Edad
                          </label>
                          <input
                            type="number"
                            value={passengers[index]?.age || ''}
                            onChange={(e) =>
                              handlePassengerChange(index, 'age', e.target.value)
                            }
                            className="w-full px-4 py-3 border border-[#E5E7EB] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                            placeholder="Edad"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#111827] mb-2">
                            Género
                          </label>
                          <select
                            value={passengers[index]?.gender || ''}
                            onChange={(e) =>
                              handlePassengerChange(index, 'gender', e.target.value)
                            }
                            className="w-full px-4 py-3 border border-[#E5E7EB] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                          >
                            <option value="">Seleccionar</option>
                            <option value="M">Masculino</option>
                            <option value="F">Femenino</option>
                            <option value="O">Otro</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Summary */}
            <div className="col-span-4">
              <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-6 sticky top-6">
                <h3 className="font-bold text-[#111827] mb-4">Detalles del viaje</h3>

                <div className="space-y-4 pb-4 border-b border-[#E5E7EB]">
                  <div>
                    <p className="text-xs text-[#6B7280] mb-1">Compañía</p>
                    <p className="font-semibold text-[#111827]">{safeBookingData.company}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280] mb-1">Ruta</p>
                    <p className="font-semibold text-[#111827]">
                      {safeBookingData.departureLocation.split(' ')[0]} →{' '}
                      {safeBookingData.arrivalLocation.split(' ')[0]}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280] mb-1">Horario</p>
                    <p className="font-semibold text-[#111827]">
                      {safeBookingData.departureTime} - {safeBookingData.arrivalTime}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280] mb-1">Asientos seleccionados</p>
                    <div className="flex flex-wrap gap-2 mt-2">
{safeSelectedSeats.map((seat: string | number) => (
                        <span
                          key={seat}
                          className="px-3 py-1.5 bg-[#1E3A8A] text-white text-sm font-bold rounded-[8px]"
                        >
                          {seat}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pt-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#6B7280]">
                      {safeSelectedSeats.length} {safeSelectedSeats.length === 1 ? 'boleto' : 'boletos'}
                    </span>
                    <span className="font-semibold text-[#111827]">
                      ${totalPrice.toLocaleString('es-MX')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm pb-4 border-b border-[#E5E7EB]">
                    <span className="text-[#6B7280]">Impuestos</span>
                    <span className="font-semibold text-[#111827]">$0</span>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <span className="font-bold text-[#111827]">Total</span>
                    <span className="text-2xl font-bold text-[#111827]">
                      ${totalPrice.toLocaleString('es-MX')}
                    </span>
                  </div>
                </div>

                <Button
                  variant="primary"
                  className="w-full mt-6 py-3"
                  onClick={handleContinueToPayment}
                >
                  Continuar con {safeSelectedSeats.length} {safeSelectedSeats.length === 1 ? 'asiento' : 'asientos'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PassengerForm;